import React, { useState } from "react";
import { useLocation, Link } from "wouter";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { 
  Book, 
  Beaker, 
  Brain, 
  Heart, 
  Search, 
  TestTube,
  Microscope, 
  Pill, 
  Activity, 
  Menu as MenuIcon,
  X,
  ChevronRight,
  FileText,
  Database,
  LineChart,
  Lock,
  Crown
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery } from "@tanstack/react-query";

// Define our navigation structure
interface SubItem {
  title: string;
  href: string;
  description?: string;
}

type TagType = 'protocols' | 'research' | 'business' | 'tools';

interface NavItem {
  title: string;
  icon: React.ReactNode;
  href: string;
  description?: string;
  subItems?: SubItem[];
  expanded?: boolean;
  tags?: TagType[];
  available?: boolean;
}

export default function PremiumContent() {
  const [location] = useLocation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState("");
  const [navOpen, setNavOpen] = useState(!isMobile);
  
  // Tag filtering state
  const [selectedTags, setSelectedTags] = useState<TagType[]>([]);
  
  // Auth check
  const { data: authData = { isAuthenticated: false } } = useQuery<{
    isAuthenticated: boolean;
    user?: {
      id: number;
      username: string;
      email: string;
      subscriptionStatus: string;
    };
  }>({
    queryKey: ['/api/auth/status'],
    refetchOnWindowFocus: true,
    retry: false
  });

  // Generate path ID from the current URL or use default
  const currentPath = location.split("/")[2] || "overview";

  // Define the navigation items structure with expandable sections
  const [navItems, setNavItems] = useState<NavItem[]>([
    {
      title: "Peptide Protocols",
      icon: <Pill className="h-5 w-5" />,
      href: "/peptide-protocols-modular",
      description: "Comprehensive clinical guides for peptide selection, dosing protocols, and monitoring",
      expanded: true,
      available: true,
      tags: ['protocols', 'research'],
      subItems: [
        {
          title: "Thymosin Alpha-1",
          href: "/peptide-protocols-modular?peptide=immunomodulatory/thymosin-alpha-1",
          description: "Immune enhancing peptide for chronic infections and adjuvant therapy"
        },
        {
          title: "LL-37 (Cathelicidin)",
          href: "/peptide-protocols-modular?peptide=immunomodulatory/ll-37",
          description: "Antimicrobial peptide with dual wound healing properties"
        },
        {
          title: "Ipamorelin",
          href: "/peptide-protocols-modular?peptide=metabolic/ipamorelin",
          description: "Growth hormone secretagogue with selective GH release"
        },
        {
          title: "BPC-157",
          href: "/peptide-protocols-modular?peptide=additional/bpc-157",
          description: "Body Protection Compound with tissue healing properties"
        },
        {
          title: "AOD-9604",
          href: "/peptide-protocols-modular?peptide=additional/aod-9604",
          description: "GH fragment with fat-reducing effects without IGF-1 stimulation"
        }
      ]
    },
    {
      title: "Peptide Research Database",
      icon: <Database className="h-5 w-5" />,
      href: "/premium/research-database",
      description: "Searchable database of over 250 research papers on therapeutic peptides with clinical annotations",
      expanded: false,
      available: false,
      tags: ['research'],
      subItems: []
    },
    {
      title: "Optimization Protocols",
      icon: <LineChart className="h-5 w-5" />,
      href: "/premium/optimization",
      description: "Clinical workflows for hormone optimization, metabolic health, and cognitive enhancement",
      expanded: false,
      available: false,
      tags: ['protocols', 'tools'],
      subItems: []
    },
    {
      title: "Longevity Clinic Business Model",
      icon: <FileText className="h-5 w-5" />,
      href: "/premium/business-model",
      description: "Complete guide to establishing a profitable longevity medicine practice",
      expanded: false,
      available: false,
      tags: ['business'],
      subItems: []
    },
    {
      title: "Lab Interpretation Tools",
      icon: <Beaker className="h-5 w-5" />,
      href: "/premium/lab-tools",
      description: "Interactive tools for analyzing biomarkers and personalizing treatment protocols",
      expanded: false,
      available: false,
      tags: ['tools'],
      subItems: []
    }
  ]);

  // Function to toggle expand/collapse of navigation sections
  const toggleExpand = (index: number) => {
    const updatedItems = [...navItems];
    updatedItems[index].expanded = !updatedItems[index].expanded;
    setNavItems(updatedItems);
  };
  
  // Function to toggle tag selection
  const toggleTag = (tag: TagType) => {
    setSelectedTags(prevTags => {
      if (prevTags.includes(tag)) {
        return prevTags.filter(t => t !== tag);
      } else {
        return [...prevTags, tag];
      }
    });
  };

  // Filter nav items based on search and tags
  const filterByTags = (item: NavItem) => {
    if (selectedTags.length === 0) return true;
    return item.tags?.some(tag => selectedTags.includes(tag)) || false;
  };

  // Combined filtering for both search and tags
  const filteredItems = navItems.filter(filterByTags);
  
  const filteredNavItems = searchQuery
    ? filteredItems.map(item => {
        // Check if main item matches
        const mainItemMatches = item.title.toLowerCase().includes(searchQuery.toLowerCase());

        // Check if any subitems match
        const matchingSubItems = item.subItems?.filter(subItem =>
          subItem.title.toLowerCase().includes(searchQuery.toLowerCase())
        ) || [];

        // If main item or any subitems match, include this item
        if (mainItemMatches || matchingSubItems.length > 0) {
          return {
            ...item,
            expanded: true, // Expand to show matching subitems
            subItems: matchingSubItems.length > 0 ? matchingSubItems : item.subItems
          };
        }
        return null;
      }).filter(Boolean) as NavItem[]
    : filteredItems;

  // Function to handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      toast({
        title: "Empty search",
        description: "Please enter a search term",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Search results",
      description: `Found ${filteredNavItems.length} categories with matching content`
    });
  };

  // Helper function to render tags with appropriate colors
  const renderTags = (tags?: TagType[]) => {
    if (!tags || tags.length === 0) return null;
    
    const tagColors: Record<TagType, { bg: string, text: string, border: string }> = {
      'protocols': { bg: '#EBF5FB', text: '#3498DB', border: '#3498DB' },
      'research': { bg: '#E8F8F5', text: '#1ABC9C', border: '#1ABC9C' },
      'business': { bg: '#F4ECF7', text: '#8E44AD', border: '#8E44AD' },
      'tools': { bg: '#FEF9E7', text: '#F1C40F', border: '#F1C40F' }
    };
    
    return (
      <div className="flex flex-wrap gap-1 mt-2 mb-3">
        {tags.map((tag, i) => (
          <span 
            key={i}
            className="px-2 py-0.5 text-xs rounded-full"
            style={{ 
              backgroundColor: tagColors[tag].bg,
              color: tagColors[tag].text,
              border: `1px solid ${tagColors[tag].border}`
            }}
          >
            {tag.charAt(0).toUpperCase() + tag.slice(1)}
          </span>
        ))}
      </div>
    );
  };

  // Check if user has access to premium content
  // Check for all premium statuses including mentorship
  const isPremiumUser = authData.isAuthenticated && 
    (authData.user?.subscriptionStatus?.toLowerCase() === 'active' || 
     authData.user?.subscriptionStatus?.toLowerCase() === 'trialing' ||
     authData.user?.subscriptionStatus?.toLowerCase() === 'mentorship');

  if (!isPremiumUser) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="container mx-auto px-4 py-10 flex-grow flex items-center justify-center">
          <div className="bg-white border rounded-lg shadow-lg p-8 max-w-2xl w-full text-center">
            <div className="mx-auto w-16 h-16 bg-amber-100 flex items-center justify-center rounded-full mb-6">
              <Lock className="h-8 w-8 text-amber-600" />
            </div>
            <h1 className="text-3xl font-serif font-bold text-[#2C3E50] mb-4">Premium Content Access</h1>
            <p className="text-lg text-[#7F8C8D] mb-6">
              This content is available exclusively to Next Generation Medicine members. 
              Join our membership to access cutting-edge protocols, research, and clinical tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/login">
                <Button variant="outline" className="w-full sm:w-auto">
                  Sign In
                </Button>
              </Link>
              <Link href="/">
                <Button className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600">
                  View Membership Options
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-1 flex">
        {/* Mobile nav toggle */}
        {isMobile && (
          <button 
            onClick={() => setNavOpen(!navOpen)}
            className="fixed bottom-6 right-6 z-40 bg-[#3498DB] text-white p-3 rounded-full shadow-lg"
          >
            {navOpen ? <X size={24} /> : <MenuIcon size={24} />}
          </button>
        )}
        
        {/* Sidebar navigation */}
        <aside 
          className={cn(
            "w-72 bg-[#F8FAFC] border-r border-[#E2E8F0] fixed inset-y-0 pt-16 z-30 transition-transform duration-300 ease-in-out",
            navOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
          )}
        >
          <div className="p-4 space-y-4">
            <form onSubmit={handleSearch} className="relative">
              <Input
                placeholder="Search premium content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pr-10"
              />
              <button 
                type="submit"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7F8C8D]"
              >
                <Search className="h-4 w-4" />
              </button>
            </form>
            
            <div>
              <h3 className="text-sm font-medium mb-2">Filter by Type:</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => toggleTag('protocols')}
                  className={cn(
                    "px-3 py-1 text-xs rounded-full border",
                    selectedTags.includes('protocols')
                      ? "bg-[#EBF5FB] border-[#3498DB] text-[#3498DB] font-medium"
                      : "bg-white border-gray-300 text-gray-600"
                  )}
                >
                  Protocols
                </button>
                <button
                  onClick={() => toggleTag('research')}
                  className={cn(
                    "px-3 py-1 text-xs rounded-full border",
                    selectedTags.includes('research')
                      ? "bg-[#E8F8F5] border-[#1ABC9C] text-[#1ABC9C] font-medium"
                      : "bg-white border-gray-300 text-gray-600"
                  )}
                >
                  Research
                </button>
                <button
                  onClick={() => toggleTag('business')}
                  className={cn(
                    "px-3 py-1 text-xs rounded-full border",
                    selectedTags.includes('business')
                      ? "bg-[#F4ECF7] border-[#8E44AD] text-[#8E44AD] font-medium"
                      : "bg-white border-gray-300 text-gray-600"
                  )}
                >
                  Business
                </button>
                <button
                  onClick={() => toggleTag('tools')}
                  className={cn(
                    "px-3 py-1 text-xs rounded-full border",
                    selectedTags.includes('tools')
                      ? "bg-[#FEF9E7] border-[#F1C40F] text-[#F1C40F] font-medium"
                      : "bg-white border-gray-300 text-gray-600"
                  )}
                >
                  Tools
                </button>
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-1">
              <h3 className="text-sm font-medium mb-2">Content Library:</h3>
              <ScrollArea className="h-[calc(100vh-240px)]">
                <div className="pr-4 space-y-4">
                  {filteredNavItems.map((item, i) => (
                    <div key={i} className="space-y-1">
                      <button
                        onClick={() => toggleExpand(i)}
                        className="w-full flex items-center justify-between text-left px-2 py-1.5 rounded hover:bg-gray-100"
                      >
                        <div className="flex items-center">
                          <div className="mr-2 text-[#3498DB]">{item.icon}</div>
                          <span className="font-medium text-[#2C3E50]">{item.title}</span>
                        </div>
                        {item.subItems && item.subItems.length > 0 && (
                          <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${item.expanded ? 'rotate-90' : ''}`} />
                        )}
                      </button>
                      
                      {item.expanded && item.subItems && item.subItems.length > 0 && (
                        <div className="ml-8 space-y-1 mt-1 border-l-2 border-gray-200 pl-2">
                          {item.subItems.map((subItem, j) => (
                            <Link
                              key={j}
                              href={subItem.href}
                              className="block px-2 py-1.5 hover:bg-gray-100 rounded text-sm text-[#7F8C8D]"
                            >
                              {subItem.title}
                              {subItem.description && (
                                <div className="text-xs text-gray-500 mt-0.5">{subItem.description}</div>
                              )}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
        </aside>
        
        {/* Main content */}
        <main className={cn(
          "flex-1 transition-all duration-300 ease-in-out",
          navOpen ? "md:ml-72" : "ml-0"
        )}>
          <div className="container mx-auto px-4 py-8 max-w-5xl">
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50]">Premium Content Library</h1>
                <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-3 py-1 rounded-full flex items-center text-sm">
                  <Crown className="h-4 w-4 mr-1" />
                  <span>Premium Access</span>
                </div>
              </div>
              <p className="text-lg text-[#7F8C8D]">
                Welcome to your premium membership resources. Access exclusive clinical protocols, research databases, 
                and practice tools to enhance your longevity medicine practice.
              </p>
              
              <div className="flex flex-wrap items-center gap-3 mt-4 p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg">
                <span className="text-sm font-medium text-[#2C3E50]">Filter by:</span>
                <div className="flex items-center">
                  <button
                    onClick={() => toggleTag('protocols')}
                    className={cn(
                      "px-2 py-0.5 text-xs rounded-full border cursor-pointer",
                      selectedTags.includes('protocols')
                        ? "bg-[#EBF5FB] border-[#3498DB] text-[#3498DB] font-medium"
                        : "bg-[#EBF5FB] border-[#3498DB] text-[#3498DB] opacity-70"
                    )}
                  >
                    Protocols
                  </button>
                  <span className="ml-2 text-xs text-[#7F8C8D]">Clinical applications, dosing</span>
                </div>
                <div className="flex items-center">
                  <button
                    onClick={() => toggleTag('research')}
                    className={cn(
                      "px-2 py-0.5 text-xs rounded-full border cursor-pointer",
                      selectedTags.includes('research')
                        ? "bg-[#E8F8F5] border-[#1ABC9C] text-[#1ABC9C] font-medium"
                        : "bg-[#E8F8F5] border-[#1ABC9C] text-[#1ABC9C] opacity-70"
                    )}
                  >
                    Research
                  </button>
                  <span className="ml-2 text-xs text-[#7F8C8D]">Scientific data, literature</span>
                </div>
                <div className="flex items-center">
                  <button
                    onClick={() => toggleTag('business')}
                    className={cn(
                      "px-2 py-0.5 text-xs rounded-full border cursor-pointer",
                      selectedTags.includes('business')
                        ? "bg-[#F4ECF7] border-[#8E44AD] text-[#8E44AD] font-medium"
                        : "bg-[#F4ECF7] border-[#8E44AD] text-[#8E44AD] opacity-70"
                    )}
                  >
                    Business
                  </button>
                  <span className="ml-2 text-xs text-[#7F8C8D]">Practice management, economics</span>
                </div>
                <div className="flex items-center">
                  <button
                    onClick={() => toggleTag('tools')}
                    className={cn(
                      "px-2 py-0.5 text-xs rounded-full border cursor-pointer",
                      selectedTags.includes('tools')
                        ? "bg-[#FEF9E7] border-[#F1C40F] text-[#F1C40F] font-medium"
                        : "bg-[#FEF9E7] border-[#F1C40F] text-[#F1C40F] opacity-70"
                    )}
                  >
                    Tools
                  </button>
                  <span className="ml-2 text-xs text-[#7F8C8D]">Calculators, analytics</span>
                </div>
                {selectedTags.length > 0 && (
                  <button
                    onClick={() => setSelectedTags([])}
                    className="text-xs text-[#3498DB] hover:underline ml-auto"
                  >
                    Clear filters
                  </button>
                )}
              </div>
              
              <div className="my-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredItems.length > 0 ? (
                  <>
                    {filteredItems.filter(item => item.available).map((item, index) => (
                      <div key={index} className="bg-white border border-[#E2E8F0] rounded-lg p-6 hover:shadow-md transition-shadow">
                        <div className="flex items-center mb-2">
                          <div className="p-2 rounded-full bg-[#EBF5FB] text-[#3498DB] mr-3">
                            {item.icon}
                          </div>
                          <h3 className="font-medium text-[#2C3E50]">{item.title}</h3>
                        </div>
                        {renderTags(item.tags)}
                        <p className="text-sm text-[#7F8C8D] mb-4">{item.description}</p>
                        <div className="flex items-center">
                          <Link href={item.href}>
                            <Button className="text-white bg-[#3498DB] hover:bg-[#2980B9]">
                              Access Content
                            </Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                    
                    {filteredItems.filter(item => !item.available).map((item, index) => (
                      <div key={index + filteredItems.filter(item => item.available).length} 
                        className="bg-white border border-[#E2E8F0] rounded-lg p-6 hover:shadow-md transition-shadow relative">
                        <div className="absolute top-4 right-4 bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full">
                          Coming Soon
                        </div>
                        <div className="flex items-center mb-2">
                          <div className="p-2 rounded-full bg-[#EBF5FB] text-[#3498DB] mr-3">
                            {item.icon}
                          </div>
                          <h3 className="font-medium text-[#2C3E50]">{item.title}</h3>
                        </div>
                        {renderTags(item.tags)}
                        <p className="text-sm text-[#7F8C8D] mb-4">{item.description}</p>
                        <div className="flex items-center">
                          <Button disabled variant="outline" className="opacity-70">
                            Coming Soon
                          </Button>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="col-span-2 p-8 text-center">
                    <p className="text-gray-500 mb-4">No content matches the selected filters.</p>
                    <Button onClick={() => setSelectedTags([])} variant="outline">
                      Clear Filters
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
                <h2 className="text-xl font-medium text-[#2C3E50] mb-3">New Content Updates</h2>
                <p className="text-[#7F8C8D] mb-4">
                  We're constantly adding new premium resources to enhance your practice. Check back regularly
                  to access the latest protocols and research summaries.
                </p>
                <div className="mt-4">
                  <div className="flex items-start mb-3">
                    <div className="h-6 w-6 rounded-full bg-green-100 text-green-800 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-xs">1</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-[#2C3E50]">Peptide Protocols</h3>
                      <p className="text-sm text-[#7F8C8D]">
                        Comprehensive clinical guides for peptide selection, dosing protocols, and monitoring
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-3">
                    <div className="h-6 w-6 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-xs">2</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-[#2C3E50]">Research Database</h3>
                      <p className="text-sm text-[#7F8C8D]">
                        Coming March 2025: Searchable database of over 250 research papers with clinical annotations
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="h-6 w-6 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-xs">3</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-[#2C3E50]">Optimization Protocols</h3>
                      <p className="text-sm text-[#7F8C8D]">
                        Coming April 2025: Clinical workflows for hormone optimization and metabolic health
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      
      <Footer />
    </div>
  );
}